/**
 * PHARMAKON — réclamations fournisseur: the SPA twin of the server CONFIG
 * `apps/server/src/config/reclamation.config.js` (refonte Achats · Stock WP-34; D-20, A-19, A-20;
 * design/ux/03_avoirs_bc_reclamations.md §5, §9, §10). The server derives its lists from the Prisma
 * enums; the browser cannot import Prisma, so this file repeats them once —
 * `apps/server/src/__tests__/reclamations.parity.test.js` fails the build on the first drift (PK-BUG-15).
 * Both planes read it: the pharmacy screens (WP-35) and the portal « Réclamations reçues » (WP-36).
 */

/** Prisma `StatutReclamation` — D-20: Brouillon → Envoyée → En cours → Résolue / Rejetée → Clôturée. */
export const STATUT_RECLAMATION = Object.freeze({
  BROUILLON: "BROUILLON",
  ENVOYEE: "ENVOYEE",
  EN_COURS: "EN_COURS",
  RESOLUE: "RESOLUE",
  REJETEE: "REJETEE",
  CLOTUREE: "CLOTUREE",
});

export const LIBELLES_STATUT_RECLAMATION = Object.freeze({
  BROUILLON: "Brouillon",
  ENVOYEE: "Envoyée",
  EN_COURS: "En cours",
  RESOLUE: "Résolue",
  REJETEE: "Rejetée",
  CLOTUREE: "Clôturée",
});

/** Prisma `MotifReclamation` and the D-20 labels, verbatim. */
export const MOTIF_RECLAMATION = Object.freeze({
  MANQUANT: "MANQUANT",
  CASSE: "CASSE",
  PERIME: "PERIME",
  ERREUR_PRODUIT_LOT: "ERREUR_PRODUIT_LOT",
  PRIX: "PRIX",
  DOUBLON: "DOUBLON",
  RETARD: "RETARD",
  AUTRE: "AUTRE",
});

export const LIBELLES_MOTIF_RECLAMATION = Object.freeze({
  MANQUANT: "Manquant",
  CASSE: "Cassé",
  PERIME: "Périmé",
  ERREUR_PRODUIT_LOT: "Erreur produit / lot",
  PRIX: "Prix",
  DOUBLON: "Doublon",
  RETARD: "Retard",
  AUTRE: "Autre",
});

/** The channels a réclamation leaves by (A-19, OP-03-33). */
export const CANAL_RECLAMATION = Object.freeze({ PLATEFORME: "PLATEFORME", EMAIL: "EMAIL", IMPRESSION: "IMPRESSION" });
export const LIBELLES_CANAL_RECLAMATION = Object.freeze({ PLATEFORME: "Plateforme", EMAIL: "E-mail", IMPRESSION: "Impression" });

/** Prisma `CoteReclamation`, `TypeEvenementReclamation`, `EtatPropositionReclamation`. */
export const COTE_RECLAMATION = Object.freeze({ PHARMACIE: "PHARMACIE", FOURNISSEUR: "FOURNISSEUR" });
export const TYPE_EVENEMENT_RECLAMATION = Object.freeze({
  CREATION: "CREATION",
  ENVOI: "ENVOI",
  ENVOI_ECHEC: "ENVOI_ECHEC",
  RELANCE: "RELANCE",
  PRISE_EN_CHARGE: "PRISE_EN_CHARGE",
  MESSAGE: "MESSAGE",
  PIECE_JOINTE: "PIECE_JOINTE",
  PROPOSITION: "PROPOSITION",
  PROPOSITION_ACCEPTEE: "PROPOSITION_ACCEPTEE",
  PROPOSITION_REFUSEE: "PROPOSITION_REFUSEE",
  REPONSE_CONSIGNEE: "REPONSE_CONSIGNEE",
  RESOLUTION: "RESOLUTION",
  REJET: "REJET",
  AVOIR_GENERE: "AVOIR_GENERE",
  CLOTURE: "CLOTURE",
  RETRAIT: "RETRAIT",
  // refonte M-31 (WP-88) — pharmacy-only events: « Déduire du stock maintenant », « Réintégrer au stock »
  DEDUCTION_STOCK: "DEDUCTION_STOCK",
  RESTITUTION_STOCK: "RESTITUTION_STOCK",
});
export const ETAT_PROPOSITION_RECLAMATION = Object.freeze({ EN_ATTENTE: "EN_ATTENTE", ACCEPTEE: "ACCEPTEE", REFUSEE: "REFUSEE" });

/** ux/03 §5.4 — the pharmacy journal views; ux/03 §5.8 — the portal views. */
export const VUE_JOURNAL_RECLAMATION = Object.freeze({ OUVERTES: "OUVERTES", A_TRAITER: "A_TRAITER", CLOTUREES: "CLOTUREES", TOUTES: "TOUTES" });
export const LIBELLES_VUE_JOURNAL_RECLAMATION = Object.freeze({
  OUVERTES: "Ouvertes",
  A_TRAITER: "À traiter par vous",
  CLOTUREES: "Clôturées",
  TOUTES: "Toutes",
});
export const VUE_PORTAIL_RECLAMATION = Object.freeze({ A_TRAITER: "A_TRAITER", EN_COURS: "EN_COURS", RESOLUES: "RESOLUES", REJETEES: "REJETEES", TOUTES: "TOUTES" });
export const LIBELLES_VUE_PORTAIL_RECLAMATION = Object.freeze({
  A_TRAITER: "À traiter",
  EN_COURS: "En cours",
  RESOLUES: "Résolues",
  REJETEES: "Rejetées",
  TOUTES: "Toutes",
});

/** The actions the API lists as open on a réclamation (`actions`), one word each. */
export const ACTION_RECLAMATION = Object.freeze({
  MODIFIER: "MODIFIER",
  SUPPRIMER: "SUPPRIMER",
  ENVOYER: "ENVOYER",
  RESERVER_NUMERO: "RESERVER_NUMERO",
  PRENDRE_EN_CHARGE: "PRENDRE_EN_CHARGE",
  ACCEPTER: "ACCEPTER",
  PROPOSER: "PROPOSER",
  REJETER: "REJETER",
  ACCEPTER_PROPOSITION: "ACCEPTER_PROPOSITION",
  REFUSER_PROPOSITION: "REFUSER_PROPOSITION",
  CONSIGNER: "CONSIGNER",
  GENERER_AVOIR: "GENERER_AVOIR",
  CLOTURER: "CLOTURER",
  RETIRER: "RETIRER",
  MESSAGE: "MESSAGE",
  PIECE_JOINTE: "PIECE_JOINTE",
  RELANCER: "RELANCER",
  // refonte WP-88 (D-36, A-83) — « Déduire du stock maintenant » after the send, « Réintégrer au stock »
  DEDUIRE_STOCK: "DEDUIRE_STOCK",
  REINTEGRER_STOCK: "REINTEGRER_STOCK",
});

/**
 * Prisma `DeductionStockReclamation` (refonte M-24, written by WP-88; D-36, CP §4.2) — when a claimed
 * product line takes its units out of the stock: at « Envoyer » or when the avoir is created (the
 * preselection). The two options and the state a document line shows, in the server's words (WP-89).
 */
export const DEDUCTION_STOCK_RECLAMATION = Object.freeze({ MAINTENANT: "MAINTENANT", A_LA_CREATION_AVOIR: "A_LA_CREATION_AVOIR" });
export const DEDUCTION_STOCK_RECLAMATION_DEFAUT = DEDUCTION_STOCK_RECLAMATION.A_LA_CREATION_AVOIR;
export const LIBELLES_CHOIX_DEDUCTION_RECLAMATION = Object.freeze({
  [DEDUCTION_STOCK_RECLAMATION.MAINTENANT]: "Déduire du stock maintenant",
  [DEDUCTION_STOCK_RECLAMATION.A_LA_CREATION_AVOIR]: "Déduire lors de la création de l'avoir",
});
export const LIBELLES_DEDUCTION_STOCK_RECLAMATION = Object.freeze({
  [DEDUCTION_STOCK_RECLAMATION.MAINTENANT]: "Déduit du stock",
  [DEDUCTION_STOCK_RECLAMATION.A_LA_CREATION_AVOIR]: "Déduction à la création de l'avoir",
});
/** Refonte WP-88 (A-82) — who gave a réclamation its issue (the avoir prefill's `issue.par`). */
export const ISSUE_RECLAMATION_PAR = Object.freeze({ FOURNISSEUR: "FOURNISSEUR", PHARMACIE: "PHARMACIE" });

/** OP-03-28 — the outcomes the pharmacy consigns for a non-connected fournisseur. */
export const ISSUE_CONSIGNEE = Object.freeze({ EN_COURS: "EN_COURS", RESOLUE: "RESOLUE", REJETEE: "REJETEE" });

/** ux/03 §10 — the bounds the composer enforces before the server does. */
export const RECLAMATION_BORNES = Object.freeze({
  objet_max: 160,
  description_max: 4000,
  description_min: 5,
  message_max: 4000,
  message_min: 1,
  motif_min: 5,
  motif_max: 500,
  lignes_max: 200,
  pj_max_fichiers: 5,
  pj_max_taille_octets: 5 * 1024 * 1024,
  relance_delai_heures: 24,
  page_taille: 20,
  /** WP-35 — the journal's largest page (print, CSV), the claimed-quantity cap, the B2 drawer list. */
  page_max: 100,
  quantite_max: 1_000_000,
  tiroir_max: 50,
  liens_max: 20,
});

/** OP-03-35 — « ouvertes » = Brouillon, Envoyée, En cours (the §B2 badge, the journal view, S3). */
export const STATUTS_OUVERTS_RECLAMATION = Object.freeze(["BROUILLON", "ENVOYEE", "EN_COURS"]);

/** ux/03 §5.4 — the journal's server sort whitelist (`sort_by`), the repository's own keys. */
export const TRIS_JOURNAL_RECLAMATION = Object.freeze(["date", "numero", "fournisseur", "pharmacie", "montant_reclame", "activite", "created_at"]);

/** OP-03-32 — the attachment types (decided by the server from the bytes). */
export const TYPES_PIECE_JOINTE_RECLAMATION = Object.freeze(["image/jpeg", "image/png", "application/pdf"]);
